import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  RefreshCw, Star, Maximize2, X, Sparkles, Loader2, Newspaper, ChevronRight,
} from "lucide-react";
import api, { apiErr } from "../lib/api";
import SummaryModal from "../components/SummaryModal";
import Markdown from "../components/Markdown";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";

const sentimentDot = { positive: "bg-emerald-400", neutral: "bg-sky-400", negative: "bg-rose-400" };

export default function Feed() {
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  const [index, setIndex] = useState(0);
  const [favIds, setFavIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feedSource, setFeedSource] = useState(null);
  const [active, setActive] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);

  const loadFavs = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites");
      setFavIds(new Set(data.map((f) => f.clip_id)));
    } catch {}
  }, []);

  const loadClips = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clips");
      setClips(data);
      setIndex(0);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClips();
    loadFavs();
    api.get("/digest").then(({ data }) => setDigest(data.content)).catch(() => {});
  }, [loadClips, loadFavs]);

  const refresh = async () => {
    setRefreshing(true);
    toast.info("Pulling your X feed & synthesizing with Claude…");
    try {
      const { data } = await api.post("/feed/refresh");
      setClips(data.clips);
      setIndex(0);
      setFeedSource(data.feed_source);
      setDigest(null);
      if (data.feed_source === "sample") {
        toast.warning("X API unavailable (tier/rate limit) — showing sample posts synthesized by Claude.");
      } else {
        toast.success(`Forged ${data.clips.length} fresh clips from your X feed.`);
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const toggleFavorite = async (clip) => {
    const id = clip.clip_id;
    try {
      if (favIds.has(id)) {
        await api.delete(`/favorites/${id}`);
        setFavIds((s) => { const n = new Set(s); n.delete(id); return n; });
        toast("Removed from favorites");
      } else {
        await api.post(`/favorites/${id}`);
        setFavIds((s) => new Set(s).add(id));
        toast.success("Saved to favorites");
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  const advance = () => setIndex((i) => i + 1);

  const generateDigest = async () => {
    setDigestLoading(true);
    setDigestOpen(true);
    try {
      const { data } = await api.post("/digest");
      setDigest(data.content);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setDigestOpen(false);
    } finally {
      setDigestLoading(false);
    }
  };

  const remaining = clips.slice(index);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full bg-emerald-400 ${refreshing ? "pf-live-dot" : ""}`} />
            {feedSource === "sample" ? "sample feed · claude synthesis" : "x.com live · claude synthesis"}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-50">
            Today's Clips
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="generate-digest-button"
            onClick={digest ? () => setDigestOpen(true) : generateDigest}
            variant="outline"
            className="h-11 rounded-xl border-white/10 bg-[#121721] text-zinc-200 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Sparkles className="w-4 h-4 mr-2 text-amber-400" /> Big Picture
          </Button>
          <Button
            data-testid="refresh-feed-button"
            onClick={refresh}
            disabled={refreshing}
            className="h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold transition-colors active:scale-95"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Refresh Feed</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-96 grid place-items-center">
          <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
        </div>
      ) : clips.length === 0 ? (
        <EmptyState onRefresh={refresh} refreshing={refreshing} onEdit={() => navigate("/inputs")} />
      ) : remaining.length === 0 ? (
        <AllCaught onReset={() => setIndex(0)} onRefresh={refresh} refreshing={refreshing} />
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          {/* swipe deck */}
          <div className="relative h-[460px] max-w-xl mx-auto w-full" data-testid="swipe-card-container">
            <AnimatePresence>
              {remaining.slice(0, 3).reverse().map((clip, i) => {
                const stackPos = remaining.slice(0, 3).length - 1 - i;
                const isTop = stackPos === 0;
                return (
                  <SwipeCard
                    key={clip.clip_id}
                    clip={clip}
                    isTop={isTop}
                    depth={stackPos}
                    isFavorite={favIds.has(clip.clip_id)}
                    onExpand={() => setActive(clip)}
                    onFavorite={() => { toggleFavorite(clip); advance(); }}
                    onSkip={advance}
                  />
                );
              })}
            </AnimatePresence>
          </div>

          {/* side rail */}
          <aside className="space-y-4">
            <div className="bg-[#121721] border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Progress</span>
                <span className="font-mono text-sm text-emerald-400">{index}/{clips.length}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${(index / clips.length) * 100}%` }} />
              </div>
              <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                Swipe <span className="text-emerald-400">right to save</span>, <span className="text-zinc-400">left to skip</span>, or tap a card to read the full briefing.
              </p>
            </div>
            <div className="bg-[#121721] border border-white/10 rounded-2xl p-5">
              <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Up next</span>
              <ul className="mt-3 space-y-2.5">
                {remaining.slice(1, 5).map((c) => (
                  <li key={c.clip_id} className="flex items-start gap-2 text-sm text-zinc-400">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-zinc-600 shrink-0" />
                    <span className="line-clamp-2 leading-snug">{c.headline}</span>
                  </li>
                ))}
                {remaining.length <= 1 && <li className="text-sm text-zinc-600">Last clip in the deck.</li>}
              </ul>
            </div>
          </aside>
        </div>
      )}

      <SummaryModal
        clip={active}
        open={!!active}
        onClose={() => setActive(null)}
        isFavorite={active ? favIds.has(active.clip_id) : false}
        onToggleFavorite={toggleFavorite}
        onSendToStudio={() => { if (active && !favIds.has(active.clip_id)) toggleFavorite(active); navigate("/export"); }}
      />

      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent data-testid="digest-modal" className="bg-[#121721] border-white/10 text-zinc-100 max-w-2xl p-0 overflow-hidden gap-0">
          <DialogTitle className="sr-only">The Big Picture digest</DialogTitle>
          <DialogDescription className="sr-only">Personalized briefing across your clips</DialogDescription>
          <div className="pf-card-glow p-6 border-b border-white/10 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h2 className="font-serif text-2xl font-bold text-zinc-50">The Big Picture</h2>
          </div>
          <div className="p-6 sm:p-8 max-h-[60vh] overflow-y-auto">
            {digestLoading ? (
              <div className="h-40 grid place-items-center"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
            ) : (
              <Markdown content={digest} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SwipeCard({ clip, isTop, depth, isFavorite, onExpand, onFavorite, onSkip }) {
  const [exitX, setExitX] = useState(0);
  const handleDragEnd = (_, info) => {
    if (!isTop) return;
    if (info.offset.x > 120) { setExitX(400); onFavorite(); }
    else if (info.offset.x < -120) { setExitX(-400); onSkip(); }
  };
  return (
    <motion.div
      data-testid={isTop ? "swipe-card-top" : undefined}
      className="absolute inset-0"
      style={{ zIndex: 10 - depth }}
      initial={{ scale: 0.92, y: 24, opacity: 0 }}
      animate={{ scale: 1 - depth * 0.04, y: depth * 16, opacity: 1 }}
      exit={{ x: exitX, opacity: 0, rotate: exitX > 0 ? 12 : -12, transition: { duration: 0.28 } }}
      transition={{ duration: 0.3 }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      whileDrag={{ cursor: "grabbing" }}
    >
      <div className={`h-full rounded-3xl border border-white/10 pf-card-glow p-7 sm:p-8 flex flex-col ${isTop ? "cursor-grab shadow-2xl shadow-black/40" : ""}`}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase font-mono tracking-widest text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/40">
              {clip.topic}
            </span>
            <span className="flex items-center gap-1.5 text-xs font-mono text-zinc-500">
              <span className={`w-1.5 h-1.5 rounded-full ${sentimentDot[clip.sentiment] || sentimentDot.neutral}`} />
              {clip.sentiment}
            </span>
          </div>
          <Newspaper className="w-5 h-5 text-zinc-700" />
        </div>

        <h2 data-testid="swipe-card-headline" className="font-serif text-2xl sm:text-3xl font-bold leading-[1.15] text-zinc-50 mb-4">
          {clip.headline}
        </h2>
        <p data-testid="swipe-card-summary-preview" className="text-zinc-400 leading-relaxed text-[15px] line-clamp-4">
          {clip.summary_short}
        </p>

        {clip.sources?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {clip.sources.slice(0, 3).map((s, i) => (
              <span key={i} className="text-xs font-mono text-sky-300/80">{s}</span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-6 flex items-center gap-2.5">
          <button
            data-testid="swipe-card-skip-button"
            onClick={onSkip}
            className="w-12 h-12 rounded-full border border-white/10 bg-[#0F141C] grid place-items-center text-zinc-400 hover:text-rose-300 hover:border-rose-500/40 transition-colors active:scale-90"
            aria-label="Skip"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            data-testid="swipe-card-expand-button"
            onClick={onExpand}
            className="flex-1 h-12 rounded-full bg-[#1A212E] border border-white/10 text-zinc-100 font-medium flex items-center justify-center gap-2 hover:bg-white/5 transition-colors active:scale-95"
          >
            <Maximize2 className="w-4 h-4" /> Full briefing
          </button>
          <button
            data-testid="swipe-card-favorite-button"
            onClick={onFavorite}
            className={`w-12 h-12 rounded-full border grid place-items-center transition-colors active:scale-90 ${
              isFavorite
                ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                : "bg-[#0F141C] text-zinc-400 border-white/10 hover:text-amber-300 hover:border-amber-500/40"
            }`}
            aria-label="Favorite"
          >
            <Star className={`w-5 h-5 ${isFavorite ? "fill-amber-300" : ""}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function EmptyState({ onRefresh, refreshing, onEdit }) {
  return (
    <div className="bg-[#121721] border border-white/10 rounded-3xl p-10 sm:p-16 text-center max-w-xl mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-6">
        <Newspaper className="w-8 h-8 text-emerald-400" />
      </div>
      <h2 className="font-serif text-2xl font-bold text-zinc-50 mb-2">No clips yet</h2>
      <p className="text-zinc-400 mb-7 leading-relaxed">
        Set your topics, keywords and X accounts in Mashup, then forge your first batch of news clips.
      </p>
      <div className="flex items-center justify-center gap-3">
        <Button onClick={onEdit} variant="outline" data-testid="empty-edit-inputs" className="h-11 rounded-xl border-white/10 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white transition-colors">
          Edit Mashup
        </Button>
        <Button onClick={onRefresh} disabled={refreshing} data-testid="empty-refresh-button" className="h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold transition-colors">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Forge clips
        </Button>
      </div>
    </div>
  );
}

function AllCaught({ onReset, onRefresh, refreshing }) {
  return (
    <div className="bg-[#121721] border border-white/10 rounded-3xl p-10 sm:p-16 text-center max-w-xl mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-6">
        <Sparkles className="w-8 h-8 text-emerald-400" />
      </div>
      <h2 className="font-serif text-2xl font-bold text-zinc-50 mb-2">You're all caught up</h2>
      <p className="text-zinc-400 mb-7">Review the deck again or refresh for fresh clips.</p>
      <div className="flex items-center justify-center gap-3">
        <Button onClick={onReset} variant="outline" className="h-11 rounded-xl border-white/10 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white transition-colors">
          Review again
        </Button>
        <Button onClick={onRefresh} disabled={refreshing} className="h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold transition-colors">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>
    </div>
  );
}
